import React, { forwardRef, useCallback, useImperativeHandle, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@material-ui/core/Dialog'
import CloseIcon from '@material-ui/icons/Close'
import { useSelector } from 'react-redux'
import moment from 'moment'
import Switch from 'react-switch'

import styles from './styles.module.scss'

import { getCompanyAssets, createMaintenancePlan, updateMaintenancePlan, removeMaintenancePlan } from '../../../api'
import useNotify from '../../../hooks/useNotify'

import Input from '../../Core/Input/Input'
import Label from '../../Core/InputLabel'
import InputSelect from '../../Core/InputSelect'

const scopeOptions = [{
  id: 'category',
  name: 'Category'
}, {
  id: 'asset',
  name: 'Specific Asset'
}]


const minutesToHours = (value) => {
  if (value === 0) {
    return '';
  }
  return value / 60;
};

const hoursToMinutes = (value) => {
  if (value === '') {
    return 0;
  }
  return value * 60;
}

const MaintenancePlanForm = forwardRef(({companyId}, ref) => {
  const { t } = useTranslation()
  const { show } = useNotify()

  const { categories } = useSelector(state => state.asset.options)

  const [open, setOpen] = useState(false)
  const [assets, setAssets] = useState([])
  const [form, setForm] = useState({
    category_id: null,
    asset_id: null,
    name: '',
    description: '',
    scheduled_date: moment().format('YYYY-MM-DD'),
    start_date: moment().format('YYYY-MM-DD'),
    end_date: '',
    interval_value: 1,
    interval_unit: 'month',
    requires_attachments: false,
    requires_photos: false,
    estimated_cost: 0,
    estimated_duration_minutes: 0,
    scope: 'category',
    trigger: 'specific_date',
  })

  const intervalOptions = [{
    id: 'day',
    name: t('Every {{interval}} days', { interval: form.interval_value }),
  }, {
    id: 'week',
    name: t('Every {{interval}} weeks', { interval: form.interval_value }),
  }, {
    id: 'month',
    name: t('Every {{interval}} months', { interval: form.interval_value }),
  }, {
    id: 'year',
    name: t('Every {{interval}} years', { interval: form.interval_value }),
  }, {
    id: 'no_repeat',
    name: t('No repeat')
  }]

  useEffect(() => {
    if (open) {
      getAssets()
    }
  }, [open])

  useImperativeHandle(ref, () => ({
    open: (data) => {
      setOpen(true)
      if (data) {
        setForm({
          id: data.id,
          category_id: data.category_id,
          asset_id: data.asset_id,
          name: data.name,
          description: data.description,
          scheduled_date: data.scheduled_date ? moment(data.scheduled_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
          start_date: data.start_date ? moment(data.start_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
          end_date: data.end_date ? moment(data.end_date).format('YYYY-MM-DD') : '',
          interval_value: data.interval_value,
          interval_unit: data.schedule_type === 'one_time' ? 'no_repeat' : data.interval_unit,
          requires_attachments: data.requires_attachments,
          requires_photos: data.requires_photos,
          estimated_cost: data.estimated_cost,
          estimated_duration_minutes: data.estimated_duration_minutes ? minutesToHours(data.estimated_duration_minutes) : 0,
          scope: data.category_id ? 'category' : 'asset',
          trigger: data.trigger,

        })
      }
    }
  }))

  const getAssets = async () => {
    const query = {
      
    }
    const res = await getCompanyAssets(companyId, query)
    if (Array.isArray(res)) {
      setAssets(res)
    }
  }

  const close = useCallback(() => {
    setOpen(false)
  }, [])

  const handleChangeForm = (e) => {
    const { name, value, type, checked } = e.target
    setForm(prevForm => ({
      ...prevForm,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async () => {
    const isRepeating = form.interval_unit !== 'no_repeat'
    const post = {
      ...(form.scope === 'category' ? { category_id: form.category_id } : { asset_id: form.asset_id }),
      name: form.name,
      description: form.description,
      trigger: form.trigger,
      schedule_type: isRepeating ? 'recurring' : 'one_time',
      ...(form.trigger === 'specific_date' && !isRepeating ? { scheduled_date: form.scheduled_date } : null),
      ...(form.trigger === 'specific_date' && isRepeating  ? { start_date: form.start_date } : null),
      ...(isRepeating ? {
        interval_value: form.interval_value,
        interval_unit: form.interval_unit
      } : null),
      is_active: true,
      requires_attachments: form.requires_attachments,
      requires_photos: form.requires_photos,
      estimated_cost: form.estimated_cost,
      estimated_duration_minutes: hoursToMinutes(form.estimated_duration_minutes),
    }
    if (form.id) {
      const res = await updateMaintenancePlan(companyId, form.id, post)
      if (res) {
        handleSuccess('update')
      }
      return
    }
    const res = await createMaintenancePlan(companyId, post)
    if (res) {
      handleSuccess('create')
    }
  }

  const handleRemove = async () => {
    const res = await removeMaintenancePlan(companyId, form.id)
    if (res) {
      handleSuccess('remove')
    }
  }

  const handleSuccess = (action) => {
    window.dispatchEvent(
      new CustomEvent('plan-updated', {detail: {action: action}})
    )
    switch (action) {
      case 'update':
        show(t('Maintenance plan updated successfully'))
        break
      case 'create':
        show(t('Maintenance plan created successfully'))
        break
      case 'remove':
        show(t('Maintenance plan removed successfully'))
        break
    }
    close()
  }

  return (
    <Dialog
      onClose={close}
      open={open}
      maxWidth={'md'}
      PaperProps={{ style: { width: '100%' } }}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            { t('Maintenance Plan') }
          </div>
          <div className={styles.closeButton} onClick={close}>
            <CloseIcon />
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('General')}</div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Plan name')}<span className={styles.requiredMark}>*</span></>} htmlFor='name' />
                <Input
                  id='name'
                  name='name'
                  type='text'
                  placeholder={t('Enter asset name')}
                  value={form.name}
                  onChange={handleChangeForm}
                  // error={errors.default_vat}
                  fullWidth
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Description')}</>} htmlFor='description' />
                <textarea
                  id='description'
                  name='description'
                  placeholder={t('Enter description')}
                  value={form.description}
                  onChange={handleChangeForm}
                  rows={4}
                  className={styles.textarea}
                />
              </div>
            </div>
          </div>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('Applies to')}</div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Scope')}</>} htmlFor='scope' />
                <InputSelect
                  id='scope'
                  options={scopeOptions}
                  value={form.scope}
                  name='scope'
                  onChange={handleChangeForm}
                  valueKey='id'
                  labelKey='name'
                  // error={errors.customer_id}
                />
              </div>
              <div className={styles.formItem}>
                <Label text={form.scope === 'category' ? t('Category') : t('Specific Asset')} htmlFor={form.scope === 'category' ? 'category_id' : 'asset_id'} />
                <InputSelect
                  id={form.scope === 'category' ? 'category_id' : 'asset_id'}
                  options={form.scope === 'category' ? categories : assets}
                  value={form.scope === 'category' ? form.category_id : form.asset_id}
                  name={form.scope === 'category' ? 'category_id' : 'asset_id'}
                  onChange={handleChangeForm}
                  valueKey='id'
                  labelKey='name'
                  // error={errors.customer_id}
                />
              </div>
            </div>
          </div>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('Trigger & repeat')}</div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Trigger')}</>} htmlFor='trigger' />
                <InputSelect
                  id='trigger'
                  options={[{id: 'specific_date', name: t('Specific date')}, {id: 'warranty_expiration', name: t('Warranty expiration')}]}
                  value={form.trigger}
                  name='trigger'
                  onChange={handleChangeForm}
                  valueKey='id'
                  labelKey='name'
                  // error={errors.customer_id}
                />
              </div>
              <div className={styles.formItem}>
                <Label text={<>{t('Repeat')}</>} htmlFor='interval_unit' />
                <InputSelect
                  id='interval_unit'
                  options={intervalOptions}
                  value={form.interval_unit}
                  name='interval_unit'
                  onChange={handleChangeForm}
                  valueKey='id'
                  labelKey='name'
                  // error={errors.customer_id}
                />
              </div>
            </div>
            <div className={styles.formRow}>
              {
                form.interval_unit !== 'no_repeat'
                  ? <div className={styles.formItem}>
                      <Label text={<>{t('Interval')}</>} htmlFor='interval_value' />
                      <Input
                        id='interval_value'
                        name='interval_value'
                        type='number'
                        value={form.interval_value}
                        onChange={handleChangeForm}
                        // error={errors.default_vat}
                        fullWidth
                      />
                    </div>
                  : null
              }
              {
                form.trigger === 'specific_date'
                  ? form.interval_unit === 'no_repeat'
                    ? <div className={styles.formItem}>
                        <Label text={<>{t('Scheduled date')}</>} htmlFor='scheduled_date' />
                        <Input
                          id='scheduled_date'
                          name='scheduled_date'
                          type='date'
                          value={form.scheduled_date}
                          onChange={handleChangeForm}
                          fullWidth
                        />
                      </div>
                    : <div className={styles.formItem}>
                        <Label text={<>{t('Start date')}</>} htmlFor='start_date' />
                        <Input
                          id='start_date'
                          name='start_date'
                          type='date'
                          value={form.start_date}
                          onChange={handleChangeForm}
                          fullWidth
                        />
                      </div>
                  : null
                
              }
              {
                form.trigger !== 'specific_date' || form.interval_unit === 'no_repeat'
                  ? <div className={styles.formItem} />
                  : null
              }
            </div>
          </div>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('Planning')}</div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Estimated cost')}</>} htmlFor='estimated_cost' />
                <Input
                  id='estimated_cost'
                  name='estimated_cost'
                  type='number'
                  value={form.estimated_cost}
                  onChange={handleChangeForm}
                  // error={errors.default_vat}
                  fullWidth
                />
              </div>
              <div className={styles.formItem}>
                <Label text={<>{t('Estimated duration (h)')}</>} htmlFor='estimated_duration_minutes' />
                <Input
                  id='estimated_duration_minutes'
                  name='estimated_duration_minutes'
                  type='number'
                  value={form.estimated_duration_minutes}
                  onChange={handleChangeForm}
                  // error={errors.default_vat}
                  fullWidth
                />
              </div>
            </div>
            <div className={styles.formRow}></div>
            <div className={styles.formRow}>
              <Switch
                onChange={() => handleChangeForm({target: {name: 'requires_attachments', value: !form.requires_attachments}})}
                offColor='#808F94'
                onColor='#0085FF'
                uncheckedIcon={false}
                checkedIcon={false}
                name='requires_attachments'
                checked={form.requires_attachments}
                height={21}
                width={40}
              />
              <Label text={t('Required attachments on completion')} />
            </div>
            <div className={styles.formRow}>
              <Switch
                onChange={() => handleChangeForm({target: {name: 'requires_photos', value: !form.requires_photos}})}
                offColor='#808F94'
                onColor='#0085FF'
                uncheckedIcon={false}
                checkedIcon={false}
                name='requires_photos'
                checked={form.requires_photos}
                height={21}
                width={40}
              />
              <Label text={t('Required photos on completion')} />
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          {
            form.id
              ? <button type="button" className={styles.removeButton} onClick={handleRemove}>
                  { t('Delete plan') }
                </button>
              : null
          }
          <button type="button" className={styles.cancelButton} onClick={close}>
            { t('Cancel') }
          </button>
          <button type="button" className={styles.saveButton} onClick={handleSubmit}>
            { form.id ? t('Update plan') : t('Create plan')  }
          </button>
        </div>
      </div>
    </Dialog>
  )
})

export default MaintenancePlanForm
